import React, {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import Web3 from 'web3';
import Coinbase from './wallets/Coinbase';
import MetaMask from './wallets/MetaMask';
import WalletConnectV2 from './wallets/WalletConnectV2';
import TrustWallet from './wallets/TrustWallet';

export interface IWeb3ContextProps {
  chainId: number;
  walletAddress: string;
  web3: Web3 | undefined;
  isInitialized: boolean;
  provider: any | undefined;
  connect(walletProvider: WalletProviders): Promise<void>;
  providerHasDisconnect(): boolean;
  disconnect(): Promise<void>;
  signMessage(message: string): Promise<string>;
}

interface IWeb3ContextProvider {
  infuraId?: string;
  projectId?: string;
  chainIds?: number[];
  children: ReactNode;
}

export enum WalletProviders {
  WalletConnectV2 = 'WalletConnectV2',
  Coinbase = 'Coinbase',
  MetaMask = 'MetaMask',
  TrustWallet = 'TrustWallet',
}

export const Web3Context = createContext<IWeb3ContextProps | undefined>(
  undefined
);

const Web3ContextProvider = ({
  children,
  infuraId,
  projectId,
  chainIds,
}: IWeb3ContextProvider) => {
  const [web3, setWeb3] = useState<Web3>();
  const [provider, setProvider] = useState<any>(undefined);
  const [walletAddress, setWalletAddress] = useState('');
  const [chainId, setChainId] = useState(0);
  const [walletProvider, setWalletProvider] = useState<WalletProviders>();
  const [isInitialized, setIsInitialized] = useState(false);

  const wallets = useMemo(() => {
    return {
      [WalletProviders.WalletConnectV2]: WalletConnectV2({
        projectId,
        chainIds,
      }),
      [WalletProviders.MetaMask]: MetaMask(),
      [WalletProviders.Coinbase]: Coinbase({ infuraId }),
      [WalletProviders.TrustWallet]: TrustWallet(),
    };
  }, [infuraId, projectId, chainIds]);

  useEffect(() => {
    if (web3) {
      return;
    }

    const attemptEnableProvider = async () => {
      let enabledProvider;

      for (const walletProvider of Object.values(WalletProviders)) {
        if (await wallets[walletProvider].isEnabled()) {
          enabledProvider = walletProvider;
          break;
        }
      }

      if (!enabledProvider) {
        setProvider(null);

        return;
      }

      const provider = await wallets[enabledProvider].getProvider();
      setProvider(provider);
      setWalletProvider(enabledProvider);
    };

    attemptEnableProvider();
  }, [web3, wallets]);

  useEffect(() => {
    if (provider === undefined) {
      return;
    }

    if (provider === null) {
      setIsInitialized(true);
      return;
    }

    let enablePromise;
    if (provider.isWalletConnect && provider.connected) {
      if (provider.accounts.length === 0) {
        enablePromise = provider.connect().then(() => provider.enable());
      } else {
        enablePromise = provider.enable();
      }
    } else if (!provider.wc) {
      enablePromise = provider.request({ method: 'eth_requestAccounts' });
    } else {
      enablePromise = provider.enable();
    }

    enablePromise
      .then(async () => {
        const web3 = new Web3(provider);
        setWeb3(web3);
        setWalletAddress((await web3.eth.getAccounts())[0]);
        setChainId(await web3.eth.getChainId());
        setIsInitialized(true);
      })
      .catch((e: unknown) => {
        setProvider(null);
        console.error(e);
      });

    provider.on('chainChanged', (chainId: string | number | undefined) => {
      // TODO: Move this to each implementation
      if (!chainId) {
        setChainId(0);
      }

      if (typeof chainId === 'string') {
        setChainId(parseInt(chainId, 16));
      }

      if (typeof chainId === 'number') {
        setChainId(chainId);
      }
    });

    provider.on('disconnect', () => {
      setWalletAddress('');
      setChainId(0);
    });

    provider.on('close', () => {
      setWalletAddress('');
      setChainId(0);
    });

    provider.on('accountsChanged', (accounts: string[]) => {
      accounts.length === 0 || !accounts[0].startsWith('0x')
        ? setWalletAddress('')
        : setWalletAddress(accounts[0]);
    });
  }, [provider]);

  const connect = async (walletProvider: WalletProviders) => {
    const isAvailable = wallets[walletProvider].isProviderAvailable();
    if (!isAvailable) {
      throw Error('Provider is not present');
    }

    const foundProvider = await wallets[walletProvider].getProvider();
    setWalletProvider(walletProvider);
    setProvider(foundProvider);

    // await provider.request({
    //   method: 'wallet_requestPermissions',
    //   params: [
    //     {
    //       eth_accounts: {},
    //     },
    //   ],
    // });
  };

  const disconnect = async () => {
    if (!provider || !walletProvider) {
      return;
    }

    wallets[walletProvider].disconnect(provider);
    setWalletProvider(undefined);
    setProvider(undefined);
    setWalletAddress('');
  };

  const providerHasDisconnect = (): boolean => {
    return (
      walletProvider !== undefined &&
      walletProvider !== WalletProviders.MetaMask &&
      walletProvider !== WalletProviders.TrustWallet
    );
  };

  const signMessage = async (message: string) => {
    if (!web3) {
      throw Error('Web3 is not initialized');
    }

    const newSignature = await web3.eth.personal.sign(
      message,
      walletAddress,
      ''
    );

    return newSignature;
  };

  return (
    <Web3Context.Provider
      value={{
        chainId,
        walletAddress,
        web3,
        isInitialized,
        provider,
        connect,
        signMessage,
        disconnect,
        providerHasDisconnect,
      }}
    >
      {children}
    </Web3Context.Provider>
  );
};

const useWeb3Context = (): IWeb3ContextProps => {
  const context = useContext(Web3Context);
  if (context === undefined) {
    throw new Error('useWeb3Context must be used within a Web3ContextProvider');
  }
  return context;
};

export { Web3ContextProvider, useWeb3Context };
